import { useMemo, useState } from "react";
import type {
  ConditionEqualityType,
  ConditionFilterDescriptor,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../../api/types/svTypes.ts";
import type { RelativeDateValue } from "../../../../../../utils/relativeDates.ts";
import { isRelativeDayValue } from "../../../../../../utils/relativeDates.ts";
import { useBiCommonDataContext } from "../../../../../contexts/BiCommonDataContext.ts";
import type { DateFilterState, DateMode, ExactDateRange } from "./dateFilterState.ts";
import { buildFilter, changeModeState, changePeriodTypeState, DateType, parseFilterState } from "./dateFilterState.ts";

interface UseDateFilterParams {
  field: ConditionField;
  disableEqualitySelection?: boolean;
  disableReferenceDateSelection?: boolean;
  onFilterUpdated: (filter: ConditionFilterDescriptor, isValid: boolean) => void;
}

interface UseDateFilterResult {
  mode: DateMode;
  periodType: DateType;
  dayEqualityType: ConditionEqualityType;
  exactDay: Date | null;
  exactRange: ExactDateRange;
  reference: RelativeDateValue | undefined;
  hidePeriodType: boolean;
  showModeToggle: boolean;
  showOperator: boolean;
  changeMode: (mode: DateMode) => void;
  changePeriodType: (periodType: DateType) => void;
  changeDayEqualityType: (equalityType: ConditionEqualityType) => void;
  changeExactDay: (date: Date | null) => void;
  changeExactRange: (range: ExactDateRange) => void;
  changeReference: (reference: RelativeDateValue) => ConditionFilterDescriptor;
}

export const useDateFilter = ({
  field,
  disableEqualitySelection,
  disableReferenceDateSelection,
  onFilterUpdated,
}: UseDateFilterParams): UseDateFilterResult => {
  const { editMode } = useBiCommonDataContext();
  const [state, setState] = useState<DateFilterState>(() => {
    const parsed = parseFilterState(field.config.filter);
    return disableReferenceDateSelection && parsed.mode === "reference"
      ? { ...parsed, mode: "exact", reference: undefined }
      : parsed;
  });
  const hidePeriodType = useMemo(
    () => editMode === false && field.config.parameter === true,
    [editMode, field.config.parameter]
  );

  const { mode, periodType, dayEqualityType, exactDay, exactRange, reference } = state;

  const update = (partial: Partial<DateFilterState>) => {
    const next = { ...state, ...partial };
    setState(next);
    const { filter, isValid } = buildFilter(next, field.meta.name);
    onFilterUpdated(filter, isValid);
    return filter;
  };

  const showModeToggle = disableReferenceDateSelection !== true;
  const showOperator =
    disableEqualitySelection !== true &&
    ((mode === "exact" && periodType === DateType.Date) || (mode === "reference" && isRelativeDayValue(reference)));

  return {
    mode,
    periodType,
    dayEqualityType,
    exactDay,
    exactRange,
    reference,
    hidePeriodType,
    showModeToggle,
    showOperator,
    changeMode: (nextMode) => update(changeModeState(state, nextMode)),
    changePeriodType: (nextPeriodType) => update(changePeriodTypeState(state, nextPeriodType)),
    changeDayEqualityType: (equalityType) => update({ dayEqualityType: equalityType }),
    changeExactDay: (date) => update({ exactDay: date }),
    changeExactRange: (range) => update({ exactRange: range }),
    changeReference: (nextReference) => update({ reference: nextReference }),
  };
};
